import { useState, useEffect } from 'react';
import { onSnapshot, query, collection, orderBy, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, uploadPDF, deleteFile } from '../config/firebase';

export const useEtudes = () => {
  const [etudes, setEtudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const q = query(collection(db, 'etudes'), orderBy('date', 'desc'));
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          date: doc.data().date.toDate ? doc.data().date.toDate() : new Date(doc.data().date)
        }));
        setEtudes(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Erreur Firestore:', err);
        if (err.code === 'permission-denied') {
          setError('Permission refusée. Vérifiez les règles Firestore.');
        } else {
          setError(err.message);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const createEtude = async (data, pdfFile) => {
    try {
      let pdfData = null;
      
      // Upload du PDF si fourni
      if (pdfFile) {
        setUploadProgress(10);
        pdfData = await uploadPDF(pdfFile, 'etudes', (progress) => {
          setUploadProgress(Math.round(progress));
        });
        setUploadProgress(100);
      }

      await addDoc(collection(db, 'etudes'), {
        ...data,
        pdfUrl: pdfData.url || null,
        pdfPath: pdfData.path || null,
        fileSize: pdfData.size || pdfFile.size || 0,
        date: new Date(),
        views: 0
      });
      
      setUploadProgress(0);
      return { success: true };
    } catch (err) {
      console.error('Erreur création étude:', err);
      setUploadProgress(0);
      return { success: false, error: err.message };
    }
  };

  const editEtude = async (id, data, pdfFile, deleteOldPdf = false) => {
    try {
      const etudeDoc = doc(db, 'etudes', id);
      let updateData = { ...data };

      // Si on doit supprimer l'ancien PDF ou uploader un nouveau
      if (pdfFile) {
        // Supprimer l'ancien PDF si existe
        if (deleteOldPdf && data.oldPdfPath) {
          try {
            await deleteFile(data.oldPdfPath);
          } catch (e) {
            console.warn('Ancien PDF non trouvé:', e);
          }
        }

        // Upload nouveau PDF
        setUploadProgress(10);
        const pdfData = await uploadPDF(pdfFile, 'etudes');
        setUploadProgress(100);
        
        updateData.pdfUrl = pdfData.url;
        updateData.pdfPath = pdfData.path;
        updateData.fileSize = pdfData.size;
      }

      delete updateData.oldPdfPath;
      await updateDoc(etudeDoc, updateData);
      
      setUploadProgress(0);
      return { success: true };
    } catch (err) {
      console.error('Erreur modification étude:', err);
      setUploadProgress(0);
      return { success: false, error: err.message };
    }
  };

  const removeEtude = async (id, pdfPath) => {
    try {
      // Supprimer le PDF du Storage si existe
      if (pdfPath) {
        try {
          await deleteFile(pdfPath);
        } catch (e) {
          console.warn('Fichier non trouvé dans Storage:', e);
        }
      }

      const etudeDoc = doc(db, 'etudes', id);
      await deleteDoc(etudeDoc);
      return { success: true };
    } catch (err) {
      console.error('Erreur suppression étude:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    etudes,
    loading,
    error,
    uploadProgress,
    createEtude,
    editEtude,
    removeEtude
  };
};

// Types d'études
export const TYPES_ETUDE = [
  { value: 'analyse', label: 'Analyse' },
  { value: 'recherche', label: 'Recherche' },
  { value: 'evaluation', label: 'Évaluation' },
  { value: 'enquete', label: 'Enquête' },
  { value: 'synthese', label: 'Synthèse' }
];
